import React, { useState, useEffect, useRef } from 'react';
import { CanvasComponent } from './components/Canvas/CanvasComponent';
import { Toolbar } from './components/Canvas/Toolbar';
import {
  BackgroundType,
  ToolType,
  DrawingState,
  UIState,
  Line,
  TextNote,
  LineStyle
} from './components/Canvas/types';
import { HistoryManager } from './components/UndoRedo/HistoryManager';
import './styles.css';

// ==== 1.  localStorage helpers ====
const STORAGE_KEY = 'whiteboard-state';

const loadFromStorage = (): { penColor?: string; offsetX?: number; offsetY?: number } | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveToStorage = (data: { penColor: string; offsetX: number; offsetY: number }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
};

// ==== 2.  обратимая инверсия яркости ====
const invertColorBrightness = (color: string, isDark: boolean): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const invert = (c: number) =>
    isDark
      ? Math.min(255, Math.floor(c + (255 - c) * 0.7)) // светлее
      : Math.max(0, Math.floor(c - (255 - c) * 0.7)); // темнее

  const newR = invert(r);
  const newG = invert(g);
  const newB = invert(b);

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

// ==== 3.  компонент ====
function App() {
  const saved = loadFromStorage();

  const [backgroundType, setBackgroundType] = useState<BackgroundType>('grid');

  const [drawingState, setDrawingState] = useState<DrawingState>({ lines: [], textNotes: [] });

  const [uiState, setUiState] = useState<UIState>({
    offsetX: saved?.offsetX ?? 0,
    offsetY: saved?.offsetY ?? 0,
    scale: 1,
    tool: 'pointer',
    penColor: saved?.penColor ?? '#000000',
    penWidth: 3,
    eraserSize: 30,
    lineStyle: 'solid',
    selection: [],
    isSelecting: false,
    isDraggingSelection: false
  });

  const historyManagerRef = useRef<HistoryManager | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // ==== 4.  история и хранилище ====
  useEffect(() => {
    historyManagerRef.current = new HistoryManager(drawingState);
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  useEffect(() => {
    saveToStorage({
      penColor: uiState.penColor,
      offsetX: uiState.offsetX,
      offsetY: uiState.offsetY
    });
  }, [uiState.penColor, uiState.offsetX, uiState.offsetY]);

  // ==== 5.  обработчики ====
  const handleToolChange = (tool: ToolType) => setUiState(prev => ({ ...prev, tool }));
  const handleColorChange = (color: string) => setUiState(prev => ({ ...prev, penColor: color }));
  const handleWidthChange = (width: number) => setUiState(prev => ({ ...prev, penWidth: width }));
  const handleLineStyleChange = (style: LineStyle) => {
    uiState.selection.length
      ? handleLineStylesChange(style)
      : setUiState(prev => ({ ...prev, lineStyle: style }));
  };
  const handleLineStylesChange = (style: LineStyle) => {
    const newLines = drawingState.lines.map(line =>
      uiState.selection.includes(line.id) ? { ...line, lineStyle: style } : line
    );
    handleUpdateLines(newLines, drawingState.textNotes);
    setUiState(prev => ({ ...prev, lineStyle: style }));
  };

  const handleBackgroundChange = (type: BackgroundType) => {
    const isDark = type === 'black';
    const newColor = invertColorBrightness(uiState.penColor, isDark);
    setBackgroundType(type);
    setUiState(prev => ({ ...prev, penColor: newColor }));
  };

  const handleUiStateChange = (newUiState: UIState) => setUiState(newUiState);

  const handleAddLine = (line: Line) => {
    const newDrawingState = { ...drawingState, lines: [...drawingState.lines, line] };
    setDrawingState(newDrawingState);
    historyManagerRef.current?.push(newDrawingState);
    setCanUndo(true); setCanRedo(false);
  };

  const handleDeleteLine = (lineId: string) => {
    const newDrawingState = { ...drawingState, lines: drawingState.lines.filter(l => l.id !== lineId) };
    setDrawingState(newDrawingState);
    historyManagerRef.current?.push(newDrawingState);
    setCanUndo(true); setCanRedo(false);
  };

  const handleUpdateLines = (lines: Line[], textNotes?: TextNote[]) => {
    const newDrawingState = { lines: [...lines], textNotes: textNotes || drawingState.textNotes || [] };
    setDrawingState(newDrawingState);
    historyManagerRef.current?.push(newDrawingState);
    setCanUndo(true); setCanRedo(false);
  };

  const handleLineColorChange = (color: string) => {
    const newLines = drawingState.lines.map(line =>
      uiState.selection.includes(line.id) ? { ...line, color } : line
    );
    const newTextNotes = (drawingState.textNotes || []).map(note =>
      uiState.selection.includes(note.id) ? { ...note, color } : note
    );
    handleUpdateLines(newLines, newTextNotes);
    setUiState(prev => ({ ...prev, penColor: color }));
  };

  const handleLineWidthChange = (width: number) => {
    const newLines = drawingState.lines.map(line =>
      uiState.selection.includes(line.id) ? { ...line, width } : line
    );
    const newTextNotes = (drawingState.textNotes || []).map(note =>
      uiState.selection.includes(note.id) ? { ...note, fontSize: width * 3 } : note
    );
    handleUpdateLines(newLines, newTextNotes);
    setUiState(prev => ({ ...prev, penWidth: width }));
  };

  const handleUndo = () => {
    const newState = historyManagerRef.current?.undo();
    if (newState) {
      setDrawingState(newState.present);
      setCanUndo(historyManagerRef.current!.canUndo());
      setCanRedo(historyManagerRef.current!.canRedo());
    }
  };

  const handleRedo = () => {
    const newState = historyManagerRef.current?.redo();
    if (newState) {
      setDrawingState(newState.present);
      setCanUndo(historyManagerRef.current!.canUndo());
      setCanRedo(historyManagerRef.current!.canRedo());
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '1') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'pointer' })); }
      if (e.key === '2') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'hand' })); }
      if (e.key === '3') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'eraser' })); }
      if (e.key === '4') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'pen' })); }
      if (e.key === '5') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'line' })); }
      if (e.key === '6') { e.preventDefault(); setUiState(p => ({ ...p, tool: 'text' })); }

      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (isCtrlOrCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if (isCtrlOrCmd && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); handleRedo(); }

      if (e.key === 'Delete' && uiState.tool === 'pointer' && uiState.selection.length > 0) {
        e.preventDefault();
        const newLines = drawingState.lines.filter(l => !uiState.selection.includes(l.id));
        const newTextNotes = (drawingState.textNotes || []).filter(n => !uiState.selection.includes(n.id));
        handleUpdateLines(newLines, newTextNotes);
        setUiState(p => ({ ...p, selection: [] }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uiState, drawingState]);

  const isDarkTheme = backgroundType === 'black';

  return (
    <div className={`app ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <header className={`app-header ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
        <Toolbar
          currentTool={uiState.tool}
          onToolChange={handleToolChange}
          penColor={uiState.penColor}
          onColorChange={handleColorChange}
          penWidth={uiState.penWidth}
          onWidthChange={handleLineWidthChange}
          lineStyle={uiState.lineStyle}
          onLineStyleChange={handleLineStyleChange}
          onLineStylesChange={handleLineStylesChange}
          backgroundType={backgroundType}
          onBackgroundChange={handleBackgroundChange}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={handleUndo}
          onRedo={handleRedo}
          selectedLines={uiState.selection}
          onLineColorChange={handleLineColorChange}
          onLineWidthChange={handleLineWidthChange}
        />
      </header>

      <main className="canvas-area">
        <CanvasComponent
          backgroundType={backgroundType}
          drawingState={drawingState}
          uiState={uiState}
          onUiStateChange={handleUiStateChange}
          onAddLine={handleAddLine}
          onDeleteLine={handleDeleteLine}
          onUpdateLines={handleUpdateLines}
        />
      </main>
    </div>
  );
}

export default App;